import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Table, Spin, Alert, Progress, List, Badge, Space, Tag, Collapse, Divider, Button } from 'antd';
import { 
  BankOutlined, 
  TeamOutlined, 
  MessageOutlined, 
  RocketOutlined, 
  WifiOutlined,
  AuditOutlined,
  UpOutlined,
  DownOutlined,
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DesktopOutlined,
  FileOutlined,
  LinkOutlined,
  TrophyOutlined,
  UserOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { schoolAPI, broadcastAPI, classAPI, teacherAPI } from '../services/api';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import TeacherWorkspaceDashboard from '../components/dashboard/TeacherWorkspaceDashboard';
import SchoolAdminDashboard from '../components/dashboard/SchoolAdminDashboard';
import PlatformOperationsDashboard from '../components/dashboard/PlatformOperationsDashboard';
import SystemAdminDashboardV3 from '../components/dashboard/SystemAdminDashboardV3';

const { Title, Text } = Typography;

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
};

// 自定义样式
const styles = {
  expandedCardContainer: {
    width: '100%',
    marginBottom: 16
  },
  expandedCard: {
    width: '100%',
    transition: 'all 0.3s'
  },
  normalCardContainer: {
    height: '100%'
  },
  dashboardCardStyle: {
    cursor: 'pointer',
    transition: 'all 0.3s',
    height: '100%'
  }
};

const LegacyDashboard = () => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schoolStats, setSchoolStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolListLoading, setSchoolListLoading] = useState(false);
  const [schoolPagination, setSchoolPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [broadcastStats, setBroadcastStats] = useState(null);
  
  // 状态变量，控制哪个卡片展开显示详情
  const [expandedCard, setExpandedCard] = useState(null);
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const canViewSystemMonitor = Boolean(
    user?.username === 'admin' ||
    user?.role === 'super_admin' ||
    user?.role === 'platform_admin'
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      if (user?.role === 'school_admin') {
        setLoading(false);
        return;
      }

      try {
        // 系统管理员的广播统计与学校列表互不依赖，并行读取，避免串行等待。
        const [broadcastResponse, schoolsResponse] = await Promise.all([
          broadcastAPI.getDashboardData(),
          isSuperAdmin() ? schoolAPI.getAll({ page: 1, pageSize: 5, summaryMode: 'basic' }) : Promise.resolve(null)
        ]);
        if (broadcastResponse.data.success) {
          // 确保所有可能为undefined的属性都有默认值
          const broadcastData = broadcastResponse.data.data || {};
          setBroadcastStats({
            totalClasses: broadcastData.totalClasses || 0,
            onlineClasses: broadcastData.onlineClasses || 0,
            totalTeachers: broadcastData.totalTeachers || 0,
            activeTeachers: broadcastData.activeTeachers || 0,
            totalStudents: broadcastData.totalStudents || 0,
            totalContacts: broadcastData.totalContacts || 0,
            totalSchools: broadcastData.totalSchools || 0,
            totalMessages: broadcastData.totalMessages || broadcastData.total || 0,
            recentMessages: broadcastData.recentMessages || broadcastData.recent || [],
            homeworkStats: broadcastData.homeworkStats || {},
            attendanceStats: broadcastData.attendanceStats || {},
            pointsStats: broadcastData.pointsStats || {},
            todayOperations: broadcastData.todayOperations || null
          });
        }

        if (isSuperAdmin()) {
          // 超级管理员: 获取所有学校
          if (schoolsResponse?.data?.success) {
            setSchools(schoolsResponse.data.data);
            setSchoolPagination((current) => ({
              ...current,
              ...(schoolsResponse.data.pagination || {}),
              pageSize: 5
            }));
          }
        } else if (user?.schoolId) {
          // 学校管理员: 获取自己学校的信息 (不使用不存在的getStats方法)
          try {
            const schoolResponse = await schoolAPI.getById(user.schoolId);
            if (schoolResponse.data.success) {
              const broadcastData = broadcastResponse.data.success ? broadcastResponse.data.data : null;
              setSchoolStats({
                school: schoolResponse.data.data,
                stats: {
                  classCount: broadcastData?.totalClasses || 0,
                  adminCount: 0, // 可以从广播统计获取或默认为0
                  messageCount: broadcastData?.totalMessages || broadcastData?.total || 0
                }
              });
            }
          } catch (err) {
            console.error('获取学校信息失败:', err);
          }
        }
      } catch (err) {
        console.error('获取数据失败:', err);
        setError('获取数据失败，请稍后再试');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user?.id, user?.schoolId, user?.role, user?.username]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSchoolPage = async (page, pageSize = 5) => {
    setSchoolListLoading(true);
    try {
      const response = await schoolAPI.getAll({ page, pageSize, summaryMode: 'basic' });
      if (response.data.success) {
        setSchools(response.data.data || []);
        setSchoolPagination({
          current: response.data.pagination?.current || page,
          pageSize,
          total: Number(response.data.pagination?.total || 0)
        });
      }
    } catch (requestError) {
      console.error('获取学校分页数据失败:', requestError);
    } finally {
      setSchoolListLoading(false);
    }
  };

  // 获取详情数据的函数
  const fetchOnlineClassesDetail = async () => {
    setDetailLoading(true);
    try {
      const response = await classAPI.getAll({ status: 'online', pageSize: 200 });
      if (response.data.success) {
        // 过滤出在线班级
        const onlineClasses = response.data.data.filter(cls => cls.status === 'online');
        setDetailData(onlineClasses);
      }
    } catch (error) {
      console.error('获取在线班级详情失败:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchClassesDetail = async () => {
    setDetailLoading(true);
    try {
      // The class list endpoint applies the same platform/school-admin/teacher
      // scope as the API. A teacher must not expand the dashboard through the
      // school-wide class endpoint merely because their profile has a schoolId.
      const response = await classAPI.getAll({ pageSize: 200 });
      if (response.data.success) {
        setDetailData(response.data.data);
      }
    } catch (error) {
      console.error('获取班级详情失败:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchTeachersDetail = async () => {
    setDetailLoading(true);
    try {
      const response = await teacherAPI.getAll();
      if (response.data.success) {
        setDetailData(response.data.data);
      }
    } catch (error) {
      console.error('获取教师详情失败:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchMessagesDetail = async () => {
    setDetailLoading(true);
    try {
      const response = await broadcastAPI.getMessageHistory();
      if (response.data.success) {
        setDetailData(response.data.data);
      }
    } catch (error) {
      console.error('获取消息详情失败:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchHomeworkDetail = async () => {
    setDetailLoading(true);
    try {
      const response = await api.get('/homework-assignments', {
        params: { page: 1, pageSize: 20 }
      });
      if (response.data.success) {
        setDetailData(response.data.data || []);
      }
    } catch (error) {
      console.error('获取作业详情失败:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchAttendanceDetail = async () => {
    setDetailLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get('/attendance/statistics', {
        params: { startDate: today, endDate: today }
      });
      if (response.data.success) {
        setDetailData(response.data.data || []);
      }
    } catch (error) {
      console.error('获取考勤详情失败:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchPointsDetail = async () => {
    setDetailLoading(true);
    try {
      const response = await api.get('/student-points', {
        params: { page: 1, pageSize: 20 }
      });
      if (response.data.success) {
        setDetailData(response.data.data || []);
      }
    } catch (error) {
      console.error('获取积分详情失败:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  // 处理仪表盘点击事件
  const handleDashboardClick = (type) => {
    // 如果已经展开，则关闭
    if (expandedCard === type) {
      setExpandedCard(null);
      return;
    }

    // 否则，展开并加载数据
    setExpandedCard(type);
    
    switch (type) {
      case 'schools':
        setDetailData(schools);
        break;
      case 'onlineClasses':
        fetchOnlineClassesDetail();
        break;
      case 'allClasses':
        fetchClassesDetail();
        break;
      case 'teachers':
        fetchTeachersDetail();
        break;
      case 'messages':
        fetchMessagesDetail();
        break;
      case 'homework':
        fetchHomeworkDetail();
        break;
      case 'attendance':
        fetchAttendanceDetail();
        break;
      case 'points':
        fetchPointsDetail();
        break;
      case 'system':
        setDetailData([
          {id: 1, name: '广播系统', status: '正常运行中', version: 'v2.0.0', uptime: '99.9%'},
          {id: 2, name: 'WebSocket服务', status: '连接正常', uptime: '99.8%'},
          {id: 3, name: '数据库服务', status: '运行正常', uptime: '100%'}
        ]);
        break;
      case 'admins':
        setDetailData([{id: 1, name: '系统管理员', role: 'super_admin'}]);
        break;
      default:
        return;
    }
  };

  // 学校列表表格列
  const columns = [
    {
      title: '学校名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: '联系人',
      dataIndex: 'contact',
      key: 'contact',
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
  ];

  // 班级表格列
  const classColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '班级名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '所属学校',
      dataIndex: 'school_name',
      key: 'school_name',
    },
    {
      title: '年级',
      dataIndex: 'grade',
      key: 'grade',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'online' ? 'green' : status === 'offline' ? 'volcano' : 'red'}>
          {status === 'online' ? '在线' : status === 'offline' ? '离线' : '已停用'}
        </Tag>
      ),
    },
  ];

  // 消息表格列
  const messageColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '发送者',
      dataIndex: 'sender_name',
      key: 'sender_name',
    },
    {
      title: '目标班级',
      dataIndex: 'class_name',
      key: 'class_name',
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: '发送时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
  ];

  // 作业表格列
  const homeworkColumns = [
    {
      title: '科目',
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: '作业标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
    },
    {
      title: '已提交',
      dataIndex: 'submitted_count',
      key: 'submitted_count',
      render: (text, record) => `${text || 0} / ${record.total_students || 0}`,
    },
    {
      title: '已批改',
      dataIndex: 'graded_count',
      key: 'graded_count',
    },
    {
      title: '布置日期',
      dataIndex: 'assign_date',
      key: 'assign_date',
    },
    {
      title: '截止日期',
      dataIndex: 'due_date',
      key: 'due_date',
    },
  ];

  // 考勤表格列
  const attendanceColumns = [
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
    },
    {
      title: '日期',
      dataIndex: 'attendance_date',
      key: 'attendance_date',
    },
    {
      title: '出勤人数',
      dataIndex: 'present_count',
      key: 'present_count',
    },
    {
      title: '缺勤人数',
      dataIndex: 'absent_count',
      key: 'absent_count',
    },
    {
      title: '迟到人数',
      dataIndex: 'late_count',
      key: 'late_count',
    },
    {
      title: '出勤率',
      dataIndex: 'attendance_rate',
      key: 'attendance_rate',
      render: (text) => text ? `${text}%` : '-',
    },
  ];

  // 积分表格列
  const pointsColumns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
    },
    {
      title: '积分',
      dataIndex: 'points',
      key: 'points',
      render: (points) => (
        <span style={{ color: points > 0 ? '#52c41a' : points < 0 ? '#ff4d4f' : '#999' }}>
          {points > 0 ? '+' : ''}{points}
        </span>
      ),
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
  ];

  // 渲染详情内容
  const renderDetailContent = (type) => {
    if (detailLoading) {
      return <Spin tip="加载中..." />;
    }

    switch (type) {
      case 'schools':
        return (
          <Table 
            columns={columns} 
            dataSource={(detailData || []).map(item => ({ ...item, key: item.id }))}
            pagination={{ pageSize: 10 }}
            bordered
            size="small"
          />
        );
      case 'onlineClasses':
      case 'allClasses':
        return (
          <Table 
            columns={classColumns} 
            dataSource={(detailData || []).map(item => ({ ...item, key: item.id }))}
            pagination={{ pageSize: 10 }}
            bordered
            size="small"
          />
        );
      case 'teachers':
        return (
          <Table 
            columns={classColumns} 
            dataSource={(detailData || []).map(item => ({ ...item, key: item.id }))}
            pagination={{ pageSize: 10 }}
            bordered
            size="small"
          />
        );
      case 'messages':
        return (
          <Table 
            columns={messageColumns} 
            dataSource={(detailData || []).map(item => ({ ...item, key: item.id }))}
            pagination={{ pageSize: 10 }}
            bordered
            size="small"
          />
        );
      case 'homework':
        return (
          <Table 
            columns={homeworkColumns} 
            dataSource={(detailData || []).map(item => ({ ...item, key: item.id }))}
            pagination={{ pageSize: 10 }}
            bordered
            size="small"
          />
        );
      case 'attendance':
        return (
          <Table 
            columns={attendanceColumns} 
            dataSource={(detailData || []).map(item => ({ ...item, key: item.id || item.attendance_date }))}
            pagination={{ pageSize: 10 }}
            bordered
            size="small"
          />
        );
      case 'points':
        return (
          <Table 
            columns={pointsColumns} 
            dataSource={(detailData || []).map(item => ({ ...item, key: item.id }))}
            pagination={{ pageSize: 10 }}
            bordered
            size="small"
          />
        );
      case 'system':
        return (
          <List
            size="small"
            itemLayout="horizontal"
            dataSource={detailData || []}
            renderItem={item => (
              <List.Item>
                <List.Item.Meta
                  title={item.name}
                  description={`状态: ${item.status}, 可用率: ${item.uptime}`}
                />
              </List.Item>
            )}
          />
        );
      default:
        return <div>暂无详细数据</div>;
    }
  };

  // 渲染单个指标卡片
  const renderStatCard = (type, title, value, prefix, color, extra = null) => {
    const isExpanded = expandedCard === type;
    
    // 基本卡片内容
    const cardContent = (
      <>
        <Statistic
          value={value}
          prefix={prefix}
          valueStyle={{ color }}
        />
        {extra && <div style={{ marginTop: 10 }}>{extra}</div>}
      </>
    );
    
    // 展开状态的卡片
    if (isExpanded) {
      return (
        <div style={styles.expandedCardContainer}>
          <Card 
            style={styles.expandedCard}
            hoverable
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{title}</span>
                <Button 
                  type="link" 
                  icon={<UpOutlined />} 
                  onClick={() => handleDashboardClick(type)}
                  size="small"
                />
              </div>
            }
          >
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8} lg={6}>
                {cardContent}
              </Col>
              <Col xs={24} sm={12} md={16} lg={18}>
                <Divider style={{ margin: '0 0 16px 0' }} />
                <div className="detail-content">
                  {renderDetailContent(type)}
                </div>
              </Col>
            </Row>
          </Card>
        </div>
      );
    }
    
    // 常规状态的卡片
    return (
      <div style={styles.normalCardContainer}>
        <Card 
          style={styles.dashboardCardStyle}
          hoverable
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{title}</span>
              <Button 
                type="link" 
                icon={<DownOutlined />} 
                onClick={(e) => { e.stopPropagation(); handleDashboardClick(type); }}
                size="small"
              />
            </div>
          }
          onClick={() => handleDashboardClick(type)}
        >
          {cardContent}
        </Card>
      </div>
    );
  };

  // 主仪表盘渲染
  const renderDashboard = () => {
    // 安全获取数据
    const safeStats = broadcastStats || {};
    const totalClasses = isSuperAdmin() ? (safeStats.totalClasses || 0) : (schoolStats?.stats?.classCount || 0);
    const onlineClasses = safeStats.onlineClasses || 0;
    const totalTeachers = safeStats.totalTeachers || 0;
    const onlineTeachers = safeStats.activeTeachers || 0;
    const totalStudents = safeStats.totalStudents || 0; // 确保学生数据正确获取
    const totalContacts = safeStats.totalContacts || 0; // 确保通讯录数据正确获取
    const totalSchools = isSuperAdmin() ? (safeStats.totalSchools || 0) : 1;
    const totalMessages = isSuperAdmin() ? (safeStats.totalMessages || 0) : (schoolStats?.stats?.messageCount || 0);
    
    const onlineClassesPercent = totalClasses > 0 ? Math.round((onlineClasses / totalClasses) * 100) : 0;
    const onlineTeachersPercent = totalTeachers > 0 ? Math.round((onlineTeachers / totalTeachers) * 100) : 0;
    
    // 获取新增统计数据
    const homeworkStats = safeStats.homeworkStats || {};
    const attendanceStats = safeStats.attendanceStats || {};
    const pointsStats = safeStats.pointsStats || {};
    const todayOperations = safeStats.todayOperations || null;
    
    // 计算作业完成率
    const homeworkCompletionRate = homeworkStats.total > 0 
      ? Math.round((homeworkStats.submitted / homeworkStats.total) * 100) 
      : 0;
    
    // 创建卡片配置数组
    const cardConfigs = [
      {
        type: 'allClasses',
        title: '班级总数',
        value: totalClasses,
        prefix: <TeamOutlined />,
        color: '#3f8600',
        extra: (
          <>
            <Text>在线班级: {onlineClasses} / {totalClasses}</Text>
            <Progress 
              percent={onlineClassesPercent} 
              status={onlineClassesPercent > 0 ? "active" : "exception"} 
            />
          </>
        )
      },
      {
        type: 'onlineClasses',
        title: '在线班级',
        value: onlineClasses,
        prefix: <WifiOutlined />,
        color: '#1890ff'
      },
      {
        type: 'teachers',
        title: '教师总数',
        value: totalTeachers,
        prefix: <AuditOutlined />,
        color: '#eb2f96',
        extra: (
          <>
            <Text>活跃教师: {onlineTeachers} / {totalTeachers}</Text>
            <Progress 
              percent={onlineTeachersPercent} 
              status={onlineTeachersPercent > 0 ? "active" : "exception"} 
            />
          </>
        )
      },
      {
        type: 'students',
        title: '学生总数',
        value: totalStudents,
        prefix: <TeamOutlined />,
        color: '#52c41a'
      },
      {
        type: 'contacts',
        title: '通讯录数据',
        value: totalContacts,
        prefix: <TeamOutlined />,
        color: '#fa541c'
      },
      {
        type: 'messages',
        title: '广播消息',
        value: totalMessages,
        prefix: <MessageOutlined />,
        color: '#722ed1'
      },
      {
        type: 'system',
        title: '系统状态',
        value: 'v2.0.0',
        prefix: <RocketOutlined />,
        color: '#52c41a',
        extra: (
          <>
            <Text>广播系统运行中</Text>
            <Progress percent={100} status="success" size="small" />
            {canViewSystemMonitor && (
              <Button
                size="small"
                type="link"
                style={{ padding: 0, marginTop: 8 }}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate('/system-monitor');
                }}
              >
                查看系统监控
              </Button>
            )}
          </>
        )
      },
      // 作业统计
      {
        type: 'homework',
        title: '作业统计',
        value: homeworkStats.total || 0,
        prefix: <BookOutlined />,
        color: '#1890ff',
        extra: (
          <>
            <Text>待批改: {homeworkStats.pending || 0}</Text>
            <Text style={{ marginLeft: 8 }}>已提交: {homeworkStats.submitted || 0}</Text>
            {homeworkStats.total > 0 && (
              <Progress 
                percent={homeworkCompletionRate} 
                status={homeworkCompletionRate >= 80 ? "success" : homeworkCompletionRate >= 50 ? "active" : "exception"}
                size="small"
                style={{ marginTop: 8 }}
              />
            )}
          </>
        )
      },
      // 考勤统计
      {
        type: 'attendance',
        title: '今日考勤',
        value: attendanceStats.todayTotal || 0,
        prefix: <UserOutlined />,
        color: attendanceStats.attendanceRate >= 95 ? '#52c41a' : attendanceStats.attendanceRate >= 90 ? '#faad14' : '#ff4d4f',
        extra: (
          <>
            <Text>出勤率: {attendanceStats.attendanceRate || 0}%</Text>
            <Text style={{ marginLeft: 8 }}>缺勤: {attendanceStats.todayAbsent || 0}</Text>
            {attendanceStats.todayTotal > 0 && (
              <Progress 
                percent={parseFloat(attendanceStats.attendanceRate || 0)} 
                status={attendanceStats.attendanceRate >= 95 ? "success" : attendanceStats.attendanceRate >= 90 ? "active" : "exception"}
                size="small"
                style={{ marginTop: 8 }}
              />
            )}
          </>
        )
      },
      // 积分统计
      {
        type: 'points',
        title: '积分统计',
        value: pointsStats.netPoints || 0,
        prefix: <TrophyOutlined />,
        color: '#faad14',
        extra: (
          <>
            <Text>获得: {pointsStats.totalEarned || 0}</Text>
            <Text style={{ marginLeft: 8 }}>扣除: {pointsStats.totalDeducted || 0}</Text>
            <Text style={{ display: 'block', marginTop: 4, fontSize: '12px', color: '#999' }}>
              参与学生: {pointsStats.totalStudents || 0}
            </Text>
          </>
        )
      }
    ];

    // 只有超级管理员才显示学校统计
    if (isSuperAdmin()) {
      cardConfigs.push({
        type: 'schools',
        title: '学校总数',
        value: totalSchools,
        prefix: <BankOutlined />,
        color: '#faad14'
      });
    }

    // 如果有展开的卡片，先渲染它
    const expandedCardConfig = cardConfigs.find(config => config.type === expandedCard);
    const normalCardConfigs = cardConfigs.filter(config => config.type !== expandedCard);
    
    return (
      <>
        {isSuperAdmin() && todayOperations && (
          <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
            <Col span={24}>
              <Card
                title="今日运营概览"
                size="small"
                extra={<Text type="secondary">服务器当天 00:00 至当前</Text>}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="新注册用户" value={todayOperations.newUsers || 0} prefix={<UserOutlined />} valueStyle={{ color: '#1677ff' }} />
                    <Text type="secondary">微信注册 {todayOperations.newWechatUsers || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="今日接入班级" value={todayOperations.activeClassesToday || 0} prefix={<TeamOutlined />} valueStyle={{ color: '#52c41a' }} />
                    <Text type="secondary">设备 {todayOperations.activeDevicesToday || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="近5分钟在线班级" value={todayOperations.onlineClasses5m || 0} prefix={<WifiOutlined />} valueStyle={{ color: '#13c2c2' }} />
                    <Text type="secondary">1分钟 {todayOperations.onlineClasses1m || 0} / 15分钟 {todayOperations.onlineClasses15m || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="桌面客户端事件" value={todayOperations.clientEvents || 0} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#722ed1' }} />
                    <Text type="secondary">心跳、注册、更新检查</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="客户端系统" value={todayOperations.desktopOsStats?.total || 0} prefix={<DesktopOutlined />} valueStyle={{ color: '#08979c' }} />
                    <Text type="secondary">Win {todayOperations.desktopOsStats?.windows || 0} / 麒麟 {todayOperations.desktopOsStats?.kylin || 0} / 统信 {todayOperations.desktopOsStats?.uos || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="学生资料" value={todayOperations.studentUploadsToday || 0} prefix={<TeamOutlined />} valueStyle={{ color: '#2f54eb' }} />
                    <Text type="secondary">今日新增学生</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="新建班级" value={todayOperations.newClassesToday || 0} prefix={<TeamOutlined />} valueStyle={{ color: '#1677ff' }} />
                    <Text type="secondary">教师今日创建</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="邀请码" value={todayOperations.invitationsToday || 0} prefix={<LinkOutlined />} valueStyle={{ color: '#722ed1' }} />
                    <Text type="secondary">已使用 {todayOperations.invitationUsesToday || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="班级待办" value={todayOperations.classTodosToday || 0} prefix={<AuditOutlined />} valueStyle={{ color: '#13c2c2' }} />
                    <Text type="secondary">收到确认 {todayOperations.classTodoFeedbackToday || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="学生联系老师" value={todayOperations.studentMessagesToday || 0} prefix={<MessageOutlined />} valueStyle={{ color: '#2f54eb' }} />
                    <Text type="secondary">今日消息</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="发送文件" value={todayOperations.uploadedFilesToday || 0} prefix={<FileOutlined />} valueStyle={{ color: '#fa8c16' }} />
                    <Text type="secondary">{formatBytes(todayOperations.uploadedBytesToday)}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="家长加入班级" value={todayOperations.parentClassJoinsToday || 0} prefix={<UserOutlined />} valueStyle={{ color: '#52c41a' }} />
                    <Text type="secondary">绑定学生 {todayOperations.parentBindingsToday || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="作业查收" value={todayOperations.homeworkReceiptSessionsToday || 0} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#eb2f96' }} />
                    <Text type="secondary">扫码确认 {todayOperations.homeworkReceiptScansToday || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="客户端版本" value={(todayOperations.desktopVersionStats || []).length} prefix={<DesktopOutlined />} valueStyle={{ color: '#08979c' }} />
                    <Text type="secondary">{(todayOperations.desktopVersionStats || []).slice(0, 3).map((item) => `${item.version} ${item.devices}`).join(' / ') || '-'}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="积分操作" value={todayOperations.pointsOperationsToday || 0} prefix={<TrophyOutlined />} valueStyle={{ color: '#d48806' }} />
                    <Text type="secondary">加 {todayOperations.pointsAddedToday || 0} / 扣 {todayOperations.pointsDeductedToday || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="智能考勤" value={todayOperations.smartAttendanceSessionsToday || 0} prefix={<VideoCameraOutlined />} valueStyle={{ color: '#13c2c2' }} />
                    <Text type="secondary">老师 {todayOperations.smartAttendanceTeachersToday || 0} 人 / 查看学生 {todayOperations.smartAttendanceViewedStudentsToday || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="课程表上传" value={todayOperations.courseScheduleUploadsToday || 0} prefix={<BookOutlined />} valueStyle={{ color: '#eb2f96' }} />
                    <Text type="secondary">课程行 {todayOperations.courseScheduleRowsToday || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="今日广播" value={todayOperations.broadcastMessages || 0} prefix={<MessageOutlined />} valueStyle={{ color: '#fa8c16' }} />
                    <Text type="secondary">发送人 {todayOperations.broadcastSenders || 0} / 目标 {todayOperations.broadcastTargets || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="广播成功" value={todayOperations.broadcastSent || 0} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
                    <Text type="secondary">系统失败 {todayOperations.broadcastFailed || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="送达日志" value={todayOperations.deliveryLogs || 0} prefix={<AuditOutlined />} valueStyle={{ color: '#1677ff' }} />
                    <Text type="secondary">已送达 {todayOperations.deliverySent || 0} / 离线未送达 {todayOperations.deliveryOffline || 0}</Text>
                  </Col>
                  <Col xs={12} sm={8} md={6} lg={4}>
                    <Statistic title="收到确认" value={todayOperations.deliveryAck || 0} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
                    <Text type="secondary">学生端/客户端 ack</Text>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        )}

        {/* 渲染展开的卡片 */}
        {expandedCardConfig && renderStatCard(
          expandedCardConfig.type,
          expandedCardConfig.title,
          expandedCardConfig.value,
          expandedCardConfig.prefix,
          expandedCardConfig.color,
          expandedCardConfig.extra
        )}
        
        {/* 渲染其他未展开的卡片 */}
        <Row gutter={[16, 16]}>
          {(normalCardConfigs || []).map(config => (
            <Col xs={24} sm={12} md={8} lg={4} key={config.type}>
              {renderStatCard(
                config.type,
                config.title,
                config.value,
                config.prefix,
                config.color,
                config.extra
              )}
            </Col>
          ))}
        </Row>

        {/* 最近消息列表 - 优化布局，固定显示6条 */}
        <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
          <Col span={24}>
            <Card 
              title="最近广播消息" 
              size="small"
              extra={
                <Space>
                  <Badge count={(safeStats.recentMessages || []).length} style={{ backgroundColor: '#52c41a' }} />
                  <Button size="small" type="link">查看全部</Button>
                </Space>
              }
            >
              {(safeStats.recentMessages || []).length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '40px 20px', fontSize: '16px' }}>
                  暂无广播消息
                </div>
              ) : (
                <Row gutter={[16, 16]}>
                  {(safeStats.recentMessages || []).slice(0, 6).map((item, index) => (
                    <Col xs={24} sm={12} md={8} lg={6} xl={4} key={item.id || index}>
                      <div style={{ 
                        padding: '16px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px',
                        background: '#fafafa',
                        height: '180px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e6f7ff';
                        e.currentTarget.style.borderColor = '#1890ff';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#fafafa';
                        e.currentTarget.style.borderColor = '#f0f0f0';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      >
                        {/* 消息头部 */}
                        <div style={{ marginBottom: '12px' }}>
                          <Space size="small" wrap>
                            <Tag size="small" color={item.sender_type === 'admin' ? 'blue' : 'green'}>
                              {item.sender_type === 'admin' ? '管理员' : '教师'}
                            </Tag>
                            <Text strong style={{ fontSize: '14px' }}>{item.sender_name}</Text>
                          </Space>
                        </div>
                        
                        {/* 目标班级 */}
                        <div style={{ marginBottom: '12px' }}>
                          <Text type="secondary" style={{ fontSize: '13px' }}>→ {item.class_name || '全校广播'}</Text>
                        </div>
                        
                        {/* 消息内容 */}
                        <div style={{ 
                          flex: 1,
                          color: '#333', 
                          fontSize: '16px', 
                          lineHeight: '1.5',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          marginBottom: '12px'
                        }}>
                          {item.content}
                        </div>
                        
                        {/* 时间 */}
                        <div style={{ marginTop: 'auto' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {new Date(item.created_at).toLocaleDateString()} {new Date(item.created_at).toLocaleTimeString().slice(0, 5)}
                          </Text>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </Card>
          </Col>
        </Row>

        {/* 超级管理员才显示学校列表 */}
        {isSuperAdmin() && (
          <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
            <Col span={24}>
              <Card title="学校列表">
                <Table
                  columns={columns}
                  dataSource={(schools || []).map(school => ({ ...school, key: school.id }))}
                  loading={schoolListLoading}
                  pagination={{
                    ...schoolPagination,
                    showSizeChanger: false,
                    onChange: loadSchoolPage
                  }}
                  bordered
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* 学校管理员显示学校信息 */}
        {!isSuperAdmin() && schoolStats?.school && (
          <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
            <Col span={24}>
              <Card title="学校信息">
                <div>
                  <p><strong>学校名称:</strong> {schoolStats.school.name}</p>
                  <p><strong>地址:</strong> {schoolStats.school.address || '未设置'}</p>
                  <p><strong>联系人:</strong> {schoolStats.school.contact || '未设置'}</p>
                  <p><strong>联系电话:</strong> {schoolStats.school.phone || '未设置'}</p>
                  <p><strong>电子邮箱:</strong> {schoolStats.school.email || '未设置'}</p>
                </div>
              </Card>
            </Col>
          </Row>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
        <p style={{ marginTop: '20px' }}>加载中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="错误"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  if (user?.role === 'school_admin') {
    return <SchoolAdminDashboard user={user} />;
  }

  if (!isSuperAdmin()) {
    return <TeacherWorkspaceDashboard user={user} broadcastStats={broadcastStats || {}} />;
  }

  return (
    <div className="dashboard">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={2}>{isSuperAdmin() ? '系统概览' : `${schoolStats?.school?.name || '学校'} 概览`}</Title>
        </Col>
      </Row>
      {renderDashboard()}
      {isSuperAdmin() && (
        <Collapse
          destroyInactivePanel
          style={{ marginTop: 20 }}
          items={[{
            key: 'platform-operations',
            label: <Text strong>平台运营与学校标准化分析</Text>,
            children: <PlatformOperationsDashboard embedded />
          }]}
        />
      )}
    </div>
  );
};

const Dashboard = () => {
  const { isSuperAdmin } = useAuth();

  if (!isSuperAdmin()) {
    return <LegacyDashboard />;
  }

  return <SystemAdminDashboardV3 legacyDashboard={<LegacyDashboard />} />;
};

export default Dashboard; 
